/**
 * The cards of a new batch, as the batch registration gathers them while the administrator
 * brings them near the phone. Reading a card only adds it to the list: nothing is written
 * until the whole batch is created, in a single request.
 */

/**
 * How long a card read again is ignored for. The phone reports the same card more than once
 * while it stays near it, so a card taken off the list while it still rests on the phone
 * would otherwise come straight back.
 */
export const RECENT_READ_WINDOW_MS = 4000;

/**
 * Builds the filter that drops a card read again within `windowMs` of its last read.
 *
 * Every read refreshes the card's time, ignored or not, so a card left resting on the phone
 * is never taken again however long it stays there.
 *
 * @returns A function telling whether a read has to be processed
 */
export function createRecentReadFilter(windowMs = RECENT_READ_WINDOW_MS) {
	const lastReads = new Map<string, number>();

	return (uid: string, now = Date.now()): boolean => {
		const last = lastReads.get(uid);
		lastReads.set(uid, now);

		return last === undefined || now - last >= windowMs;
	};
}

/**
 * Puts a card read at the top of the list, unless it is already in it: a card read again is
 * ignored, so the list has one row per card and does not jump around.
 *
 * @returns The new list, or the same one when the card was already in it
 */
export function addBatchUid(uids: string[], uid: string): string[] {
	return uids.includes(uid) ? uids : [uid, ...uids];
}

/**
 * Takes a card off the list.
 *
 * @returns The new list
 */
export function removeBatchUid(uids: string[], uid: string): string[] {
	return uids.filter((item) => item !== uid);
}
